import h from 'hyperscript';

import 'ui/form-group/form-group.css';
import { SegmentedControl } from 'ui/segmented-control/segmented-control';
import { Switch } from 'ui/switch/switch';

import { on } from 'cadenza/utils/event-util';
import { DateConditionMode } from 'cadenza/conditions/date-time-condition/date-condition-mode';
import {
  DateConditionFilterMode
} from 'cadenza/conditions/date-time-condition/date-condition-filter-mode';
import { CONDITION_SETTINGS } from 'cadenza/workbook/condition-edit-dialog/condition-settings';
import {
  DATE_RANGE_CHANGE,
  DateRangePicker
} from 'cadenza/conditions/date-range-picker/date-range-picker';
import {
  SliderStepsConfig,
  STEPS_CONFIG_CHANGE
} from 'cadenza/conditions/date-time-condition/slider-steps-config';
import {
  mostFineGrainedTickUnit
} from 'cadenza/conditions/date-time-condition/date-time-condition-utils';
import { logger } from 'cadenza/utils/logging';

import './date-time-condition-ad-hoc-settings-panel.css';
import i18n from './date-time-condition-ad-hoc-settings-panel.properties';

export const COMPONENT_NAME = 'd-date-time-condition-ad-hoc-settings-panel';
export const SETTINGS_CHANGE = 'change:settings';
export { STEPS_CONFIG_CHANGE, DATE_RANGE_CHANGE };

export class DateTimeConditionAdHocSettingsPanel extends HTMLElement {

  /** @type {DateConditionFilterMode} */
  _initialFilterMode;

  /** @type {DateConditionMode} */
  _initialSliderMode;

  /** @type {DateConditionFilterMode[]} */
  _enabledFilterModes;

  /** @type {import('cadenza/conditions/date-time-condition/date-time-condition').StepsConfig} */
  _initialStepsConfig;

  _dateConfiguration;
  _sliderValues;
  _isInterval;
  _isRelativeDate;
  _contextValueFn;

  _segmentedControl;
  _sliderSwitch;
  _totalSliderRangeElement;
  _stepsConfig;
  _stepsConfigContainer;
  _disabledMessageElement;

  /**
   * @param {object} options
   * @param {DateConditionFilterMode} options.initialFilterMode
   * @param {DateConditionMode} options.initialSliderMode
   * @param {import('cadenza/conditions/date-time-condition/date-time-condition').StepsConfig} options.initialStepsConfig
   * @param {DateConditionFilterMode[]} options.enabledFilterModes
   * @param {object} options.dateConfiguration
   * @param {object} options.sliderValues
   * @param {boolean} options.isInterval
   * @param {boolean} options.isRelativeDate
   * @param {Function} options.contextValueFn
   */
  constructor ({
    initialFilterMode,
    initialSliderMode,
    enabledFilterModes,
    initialStepsConfig,
    dateConfiguration,
    sliderValues,
    isInterval,
    isRelativeDate,
    contextValueFn
  }) {
    super();

    this._initialFilterMode = initialFilterMode;
    this._initialSliderMode = initialSliderMode;
    this._initialStepsConfig = initialStepsConfig;
    this._enabledFilterModes = enabledFilterModes;
    this._dateConfiguration = dateConfiguration;
    this._sliderValues = sliderValues;
    this._isInterval = isInterval;
    this._isRelativeDate = isRelativeDate;
    this._contextValueFn = contextValueFn;
  }

  connectedCallback () {
    if (this._sliderValues.sliderCustomStartDate && this._sliderValues.sliderCustomEndDate && this._contextValueFn) {
      this._contextValueFn().then((startEnd) => {
        this._sliderValues.sliderStartDate = startEnd.start;
        this._sliderValues.sliderEndDate = startEnd.end;
        this._initUi();
      }).catch(e => logger.error('Failed to load context sensitive data ', e));
    } else {
      this._initUi();
    }
  }

  _initUi () {
    if (this._areBothFilterModesEnabled() && this._isInterval) {
      const title = h('label', i18n('title'));

      this._segmentedControl = new SegmentedControl([
        {
          value: DateConditionFilterMode.DATA_INPUT,
          label: i18n('dataInput')
        }, {
          value: DateConditionFilterMode.STEPS,
          label: i18n('steps')
        }
      ], { ariaLabelledBy: title });
      this._segmentedControl.value = this._initialFilterMode;
      on(this._segmentedControl, 'change', () => this._onChange());

      this.append(h('form', h('form-group', [ title, this._segmentedControl ])));
    }

    this._sliderSwitch = new Switch({
      checked: this._initialSliderMode === DateConditionMode.SLIDER
    });
    const switchLabel = h('label.d-form-check', [
      this._sliderSwitch, i18n('displaySlider')
    ]);
    on(this._sliderSwitch, 'change', () => this._onChange());
    this.append(h('form', h('.form-group', switchLabel)));

    this._totalSliderRangeElement = this._createTotalSliderRange();
    this.append(this._totalSliderRangeElement);
    this._updateTotalSliderRangeVisibility();

    this._createStepsConfig();
    on(this._totalSliderRangeElement, DATE_RANGE_CHANGE, event => {
      event.stopImmediatePropagation();
      const detail = event.detail;
      // There can be null value from the event when user clears the date range.
      // In such case we use the defaultDate which is derived from data.
      const defaultDate = detail.isStart ? this._sliderValues.sliderStartDate : this._sliderValues.sliderEndDate;
      // If the date range change affects the steps configuration, pass it along with
      // the event so that a composite command can be created.
      const updatedStepsConfig = this._stepsConfig.updateDateRange(detail.isStart, detail.value ?? defaultDate);
      if (updatedStepsConfig) {
        detail.stepsConfig = this._stepsConfigToConditionSettings(updatedStepsConfig);
      }
      this.dispatchEvent(new CustomEvent(event.type, { detail }));
    });

    this._updateFilterModeDependentUi();

    this.classList.add(COMPONENT_NAME, 'd-stack-v', 'space-3');
  }

  disconnectedCallback () {
    this.innerHTML = '';
  }

  /**
   * Sets the current step config in case it has changed since opening the panel.
   *
   * @param {import('cadenza/conditions/date-time-condition/date-time-condition').StepsConfig} stepsConfig
   *  - the new steps config
   */
  setStepsConfig (stepsConfig) {
    this._stepsConfig.setStepsConfig(stepsConfig);
  }

  _createTotalSliderRange () {
    const title = h('label', i18n('range.title'));
    return h('form-group', [ title, new DateRangePicker({
      dateFormat: this._dateConfiguration.dateFormat,
      initialStartDate: this._sliderValues.sliderCustomStartDate,
      initialEndDate: this._sliderValues.sliderCustomEndDate,
      minDate: this._sliderValues.sliderStartDate,
      maxDate: this._sliderValues.sliderEndDate,
      mostFineGrainedTickUnit: mostFineGrainedTickUnit(this._dateConfiguration)
    }) ]);
  }

  _createStepsConfig () {
    const title = h('label', i18n('steps-config.title'));
    const sliderStartDate =
      this._sliderValues.sliderCustomStartDate || this._sliderValues.sliderStartDate;
    const sliderEndDate =
      this._sliderValues.sliderCustomEndDate || this._sliderValues.sliderEndDate;

    this._stepsConfig = new SliderStepsConfig(
      this._initialStepsConfig,
      mostFineGrainedTickUnit(this._dateConfiguration),
      sliderStartDate,
      sliderEndDate,
      this._isRelativeDate
    );
    on(this._stepsConfig,
      STEPS_CONFIG_CHANGE,
      event => {
        this.dispatchEvent(new CustomEvent(SETTINGS_CHANGE, {
          detail: {
            value: this._stepsConfigToConditionSettings(event.detail.stepsConfig)
          }
        }));
      });
    this._disabledMessageElement = h('d-alert.d-alert-info.d-alert-small.d-alert', {
      hidden: !this._isRelativeDate
    }, i18n('steps.disabledMessage'));
    this._stepsConfigContainer = h('form-group', [ title, this._stepsConfig ]);
    this.append(this._stepsConfigContainer, this._disabledMessageElement);
  }

  _onChange () {
    this._updateFilterModeDependentUi();
    this._updateTotalSliderRangeVisibility();
    this.dispatchEvent(new CustomEvent(SETTINGS_CHANGE, { detail: { value: this._getSettings() } }));
  }

  _getSettings () {
    return {
      [CONDITION_SETTINGS.DATE_CONDITION_ACTIVE_FILTER_MODE]: this._getCurrentFilterMode(),
      [CONDITION_SETTINGS.DATE_CONDITION_MODE]: this._getSliderMode()
    };
  }

  _getSliderMode () {
    return this._sliderSwitch.checked ? DateConditionMode.SLIDER : DateConditionMode.CALENDAR;
  }

  _updateTotalSliderRangeVisibility () {
    this._totalSliderRangeElement.toggleAttribute('hidden', !this._sliderSwitch.checked);
  }

  _updateFilterModeDependentUi () {
    const isStepsMode = this._getCurrentFilterMode() === DateConditionFilterMode.STEPS;
    this._stepsConfigContainer.toggleAttribute('hidden', !isStepsMode || !this._isInterval);

    // When steps mode is active, do not show the sliderSwitch, instead force the slider to be shown.
    // Except when "point in time" is enabled, then the switch must be shown.
    this._sliderSwitch.parentElement.toggleAttribute('hidden', isStepsMode && this._isInterval);

    // Force slider to be displayed when steps mode is active. Except when "point in time" is enabled,
    // then the slider can be switched on an off.
    if ((isStepsMode && !this._sliderSwitch.checked) && this._isInterval) {
      this._sliderSwitch.checked = true;
      this._onChange();
    }
  }

  _areBothFilterModesEnabled () {
    return this._enabledFilterModes.length === 2;
  }

  /**
   * Returns the currently selected filter mode
   *
   * @return {DateConditionFilterMode} the currently selected filter mode
   */
  _getCurrentFilterMode () {
    if (!this._segmentedControl) { // not connected yet or only one filter mode available
      return this._initialFilterMode;
    } else {
      return this._segmentedControl.value;
    }
  }

  _stepsConfigToConditionSettings (stepsConfig) {
    return {
      [CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_UNIT]: stepsConfig.unit,
      [CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_LENGTH]: stepsConfig.timeWindow,
      [CONDITION_SETTINGS.DATE_CONDITION_INTERVAL_OVERLAP_LENGTH]: stepsConfig.overlap
    };
  }

}

customElements.define(COMPONENT_NAME, DateTimeConditionAdHocSettingsPanel);
